import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

const tabs = [
  { to: "/account", label: "Επισκόπηση", emoji: "🐼" },
  { to: "/account/orders", label: "Παραγγελίες", emoji: "📦" },
  { to: "/account/addresses", label: "Διευθύνσεις", emoji: "📍" },
  { to: "/account/wishlist", label: "Αγαπημένα", emoji: "💖" },
  { to: "/account/coupons", label: "Κουπόνια μου", emoji: "🎟️" },
  { to: "/account/returns", label: "Επιστροφές", emoji: "↩️" },
  { to: "/account/security", label: "Ασφάλεια", emoji: "🔑" },
  { to: "/account/privacy", label: "Απόρρητο & δεδομένα", emoji: "🔒" },
] as const;

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/account" },{ title: "Ο λογαριασμός μου — MrPanda.gr" }] }),
  component: AccountLayout,
});

interface Tab {
  to: string;
  label: string;
  emoji: string;
}

function AccountNav({ tabs, pathname, onNavigate }: { tabs: readonly Tab[]; pathname: string; onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-1">
      {tabs.map((t) => {
        const active = pathname === t.to;
        return (
          <Link
            key={t.to}
            to={t.to}
            onClick={onNavigate}
            className={`group flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-medium transition-all ${
              active
                ? "bg-primary text-primary-foreground shadow-sm"
                : "hover:bg-muted hover:translate-x-0.5"
            }`}
          >
            <span className={`text-lg transition-transform group-hover:scale-125 ${active ? "animate-bounce" : ""}`}>
              {t.emoji}
            </span>
            {t.label}
          </Link>
        );
      })}
      <button
        onClick={() => supabase.auth.signOut()}
        className="mt-2 flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left font-medium text-destructive transition-all hover:bg-destructive/10 hover:translate-x-0.5"
      >
        <span className="text-lg">👋</span>
        Αποσύνδεση
      </button>
    </nav>
  );
}

function AccountLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const activeTab = tabs.find((t) => t.to === pathname) ?? tabs[0];

  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 pb-10 pt-6">
        <div className="mb-6 flex items-center gap-4 rounded-2xl border border-border p-5 mp-playful-panel shadow-sm">
          <span className="text-4xl animate-bob">🐼</span>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Ο λογαριασμός μου</h1>
            <p className="text-sm text-muted-foreground">Όλα τα αγαπημένα σου σε ένα μέρος 🎋</p>
          </div>
        </div>

        {/* Mobile dropdown */}
        <Collapsible open={menuOpen} onOpenChange={setMenuOpen} className="mb-4 md:hidden">
          <CollapsibleTrigger asChild>
            <button className="flex w-full items-center justify-between rounded-2xl border border-border bg-card p-4 text-sm font-medium shadow-sm">
              <span className="flex items-center gap-2.5">
                <span className="text-lg">{activeTab.emoji}</span>
                {activeTab.label}
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 rounded-2xl border border-border bg-card p-3 text-sm shadow-sm">
              <AccountNav tabs={tabs} pathname={pathname} onNavigate={() => setMenuOpen(false)} />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div className="grid gap-6 md:grid-cols-[220px_1fr]">
          <aside className="hidden h-fit rounded-2xl border border-border bg-card p-3 text-sm shadow-sm md:block">
            <AccountNav tabs={tabs} pathname={pathname} />
          </aside>
          <div><Outlet /></div>
        </div>
      </div>
    </StorefrontLayout>
  );
}
